/** @jsx jsx */
import {
  Box,
  Button,
  Col,
  Container,
  Row,
} from '@bottlebooks/gatsby-theme-base';
import { Trans } from '@lingui/macro';
import { graphql } from 'gatsby';
import React from 'react';
import { jsx } from 'theme-ui';
import { resolve } from '../../helpers/path';
import EventBanner from '../Event/EventBanner';
import EventHeader from '../Event/EventHeader';
import { ExhibitorListRow, ExhibitorsList } from '../ExhibitorsList';
import createFacets from '../Filters/Brand/brandFacets';
import ExhibitorFacetsBar, {
  getActiveFacets,
} from '../Filters/Brand/ExhibitorFacetsBar';
import reducer, {
  initialState,
  searchTermChanged,
} from '../Filters/Brand/exhibitorFilterReducer';
import ExhibitorFilters from '../Filters/Brand/ExhibitorFilters';
import ExhibitorSearchTermFacet from '../Filters/Brand/ExhibitorSearchTermFacet';
import * as sortOptions from '../Filters/Brand/exhibitorSortOptions';
import FiltersToggleButton from '../Filters/FiltersToggleButton';
import useFilter from '../Filters/useFilter';
import useSyncedLocation, {
  getStateFromLocation,
} from '../Filters/useSyncedLocation';
import Layout from '../Layout';
import ListRow from '../List/ListRow';
import SearchHeader from '../Search/SearchHeader';
import SEO from '../seo.js';
import EmptyState from './EmptyState';
import useExhibitorSearch from './useExhibitorSearch';

const PAGE_SIZE = 30;

export default function ExhibitorsPage({
  exhibitors,
  event,
  location,
  paths = {},
  facetConfig,
}) {
  // Get the state.
  const stateFromLocation = getStateFromLocation(location, initialState);
  const [state, dispatch] = React.useReducer(reducer, stateFromLocation);
  // Change the URL hash when the filters change.
  useSyncedLocation(state, { initialState, location });
  // Filter the exhibitors by term and facets.
  const searched = useExhibitorSearch(exhibitors, state.searchTerm);
  const { items, facets } = useFilter(searched, {
    state,
    facetDefinitions: createFacets(facetConfig, state),
    sortOptions,
  });
  const [limit, setLimit] = React.useState(PAGE_SIZE);
  const [filtersHidden, setFiltersHidden] = React.useState(true);
  function dispatchFilter(event, isActive) {
    dispatch(event);
    setLimit(PAGE_SIZE);
    if (isActive) setFiltersHidden(true);
  }
  const limitedItems = items.slice(0, limit);
  const hasMore = items.length > limitedItems.length;
  const hasActiveFacets = getActiveFacets(state).length > 0;
  const page = { path: location.href };
  return (
    <Layout paths={paths} sx={{ backgroundColor: 'light' }}>
      <SEO page={page} />
      <EventBanner event={event} />
      <Container sx={{ paddingY: [3, 5], marginBottom: 4 }}>
        {hasActiveFacets ? (
          <SearchHeader totalCount={exhibitors.length} count={items.length} />
        ) : (
          <EventHeader event={event} sx={{ paddingY: [3, 4] }} />
        )}
      </Container>

      <Box sx={{ backgroundColor: 'light' }}>
        <Container fluid sx={{ maxWidth: 'container.fluid' }}>
          <ExhibitorFacetsBar
            dispatch={dispatch}
            state={state}
            sx={{ marginBottom: 5 }}
          />
        </Container>
      </Box>

      <Container fluid sx={{ marginBottom: 6, maxWidth: 'container.fluid' }}>
        <Row>
          <Col sx={{ marginTop: 2, width: ['100%', null, `${500 / 12}%`] }}>
            <ExhibitorSearchTermFacet
              value={state.searchTerm}
              onChange={value => dispatch(searchTermChanged(value))}
            />
            <FiltersToggleButton
              onToggle={() => setFiltersHidden(!filtersHidden)}
              filtersHidden={filtersHidden}
              sx={{
                display: ['flex', null, 'none'],
                marginTop: -3,
                marginBottom: 3,
              }}
            />

            <ExhibitorFilters
              key={state.searchTerm}
              facets={facets}
              dispatch={dispatchFilter}
              state={state}
              totalCount={exhibitors.length}
              count={items.length}
              sx={{ display: [filtersHidden ? 'none' : null, null, 'block'] }}
            />
          </Col>
          <Col sx={{ width: ['100%', null, `${700 / 12}%`] }}>
            {!items || !items.length ? (
              <EmptyState />
            ) : (
              <ExhibitorsList sx={{ marginX: [-3, -2], marginY: -2 }}>
                {limitedItems.map(exhibitor => (
                  <ExhibitorListRow
                    sx={{ marginX: [0, 2], marginY: [0, 2] }}
                    key={exhibitor.exhibitorId}
                    to={
                      exhibitor.slug &&
                      resolve([paths.exhibitors, exhibitor.slug])
                    }
                    exhibitor={exhibitor}
                  />
                ))}
                {hasMore && (
                  <Button
                    as={ListRow}
                    variant="empty"
                    onClick={() => setLimit(limit + PAGE_SIZE)}
                    sx={{
                      margin: [0, 2],
                      paddingX: 4,
                      paddingY: 3,
                      textAlign: 'center',
                      display: 'block',
                      border: 0,

                      variant: 'text.small',
                    }}
                  >
                    <Trans>Show more</Trans>
                  </Button>
                )}
              </ExhibitorsList>
            )}
          </Col>
        </Row>
      </Container>
    </Layout>
  );
}

export const fragment = graphql`
  fragment ExhibitorsPage_Event on Event {
    ...EventBanner
    ...EventHeader
  }

  fragment ExhibitorsPage_first on Exhibitor {
    ...ExhibitorListRow_first
  }

  fragment ExhibitorsPage on Exhibitor {
    exhibitorId
    slug
    ...ExhibitorListRow
    ...Search_Exhibitor
    ...ExhibitorFilters
  }
`;
